"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { getAggregatedBilling } from "@/src/utils/billingApi"

// Types to match the existing interface
export type User = {
  id: string
  name: string
  email: string
  image?: string
  credits: number
  creditBreakdown?: {
    total: number
    planRemaining: number
    walletCredits: number
  }
  company?: string
  billingAddress?: {
    street?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
  }
  hasPassword?: boolean // ← added for new settings-page logic
}

export type Session = {
  user: User
  expires: string
}

type AuthStatus = "authenticated" | "unauthenticated" | "loading"

type SessionContextValue = {
  data: Session | null
  status: AuthStatus
  update: (userid?: string) => Promise<void>
  refreshCredits: () => Promise<void>
  signIn: () => void
  signOut: () => void
}

// Create auth context
const AuthContext = createContext<SessionContextValue>({
  data: null,
  status: "unauthenticated",
  update: async () => {},
  refreshCredits: async () => {},
  signIn: () => {},
  signOut: () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [status, setStatus] = useState<AuthStatus>("loading")
  const router = useRouter()

  // Check for JWT tokens on mount to persist auth state across refreshes
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // Read userid from localStorage
        const storedUserId = localStorage.getItem("roomvia_userid")

        if (!storedUserId) {
          setSession(null)
          setStatus("unauthenticated")
          return
        }

        const response = await fetch("/api/user_authentication/profile", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userid: storedUserId }),
        })

        if (response.ok) {
          const userData = await response.json()
          setSession({
            user: userData.user,
            expires: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          })
          setStatus("authenticated")

          if (userData.user?.id) {
            try {
              const aggregatedData = await getAggregatedBilling(userData.user.id)
              if (aggregatedData?.capacity) {
                const total = aggregatedData.capacity.effective_capacity_units || 0

                // Calculate subscription credits remaining from balances
                let subscriptionRemaining = 0
                if (aggregatedData.balances && aggregatedData.balances.length > 0) {
                  subscriptionRemaining = aggregatedData.balances.reduce((sum: number, balance: any) => {
                    return sum + Math.max(0, balance.planQuota - balance.dbCommittedThisMonth)
                  }, 0)
                }

                // Wallet credits = total - subscription remaining
                const walletCredits = Math.max(0, total - subscriptionRemaining)

                const creditBreakdown = {
                  total,
                  planRemaining: subscriptionRemaining,
                  walletCredits,
                }
                setSession((prev) =>
                  prev
                    ? {
                        ...prev,
                        user: {
                          ...prev.user,
                          credits: creditBreakdown.total,
                          creditBreakdown,
                        },
                      }
                    : null,
                )
              }
            } catch (error) {
              console.error("💥 AuthContext: Error loading initial credits:", error)
            }
          }
        } else {
          localStorage.removeItem("roomvia_userid")
          setSession(null)
          setStatus("unauthenticated")
        }
      } catch (error) {
        console.error("💥 AuthContext: Error checking auth status:", error)
        setSession(null)
        setStatus("unauthenticated")
      }
    }

    checkAuthStatus()
  }, [])

  // Sign in function - redirects to login page
  const signIn = () => {
    router.push("/login")
  }

  // Sign out function - calls the JWT logout API
  const signOut = async () => {
    try {
      const requestBody: { userid?: string } = {}

      if (session?.user?.id) {
        requestBody.userid = session.user.id
      }

      await fetch("/api/user_authentication/logout", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      })
    } catch (error) {
      console.error("💥 AuthContext: Logout error:", error)
    } finally {
      localStorage.removeItem("roomvia_userid")
      setSession(null)
      setStatus("unauthenticated")
      router.push("/")
    }
  }

  const refreshCredits = async () => {
    if (!session?.user?.id) {
      return
    }

    try {
      console.log("[v0] 🔄 Starting credit refresh for user:", session.user.id)

      let aggregatedData
      let retryCount = 0
      const maxRetries = 3

      do {
        if (retryCount > 0) {
          // Wait before retry to allow backend to update
          await new Promise((resolve) => setTimeout(resolve, 1000 * retryCount))
          console.log(`[v0] 🔄 Retrying credit refresh (attempt ${retryCount + 1}/${maxRetries})`)
        }

        aggregatedData = await getAggregatedBilling(session.user.id)
        retryCount++

        // If we have data, check if it looks fresh by comparing with current session
        if (aggregatedData?.capacity && session.user.creditBreakdown) {
          const newTotal = aggregatedData.capacity.effective_capacity_units || 0
          const currentTotal = session.user.creditBreakdown.total

          // If the total hasn't changed and we just performed an action, retry
          if (newTotal === currentTotal && retryCount <= maxRetries) {
            console.log(`[v0] 📊 Data appears stale (${newTotal} = ${currentTotal}), retrying...`)
            continue
          }
        }

        break
      } while (retryCount <= maxRetries)

      console.log("[v0] 📊 Fresh aggregated data received:", aggregatedData)

      if (aggregatedData?.capacity) {
        const total = aggregatedData.capacity.effective_capacity_units || 0

        // Calculate subscription credits remaining from balances
        let subscriptionRemaining = 0
        if (aggregatedData.balances && aggregatedData.balances.length > 0) {
          subscriptionRemaining = aggregatedData.balances.reduce((sum: number, balance: any) => {
            return sum + Math.max(0, balance.planQuota - balance.dbCommittedThisMonth)
          }, 0)
        }

        // Wallet credits = total - subscription remaining
        const walletCredits = Math.max(0, total - subscriptionRemaining)

        const creditBreakdown = {
          total,
          planRemaining: subscriptionRemaining,
          walletCredits,
        }

        console.log("[v0] 💳 Calculated credit breakdown:", creditBreakdown)

        setSession((prev) =>
          prev
            ? {
                ...prev,
                user: {
                  ...prev.user,
                  credits: creditBreakdown.total,
                  creditBreakdown,
                },
              }
            : null,
        )

        console.log("[v0] ✅ Credit refresh completed successfully")
      }
    } catch (error) {
      console.error("💥 AuthContext: Error refreshing credits:", error)
    }
  }

  const update = async (userid?: string) => {
    try {
      const requestBody: { userid?: string } = {}

      const userIdToUse = userid || session?.user?.id

      if (userIdToUse) {
        requestBody.userid = userIdToUse
      }

      const response = await fetch("/api/user_authentication/profile", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      })

      if (response.ok) {
        const userData = await response.json()

        setSession({
          user: userData.user,
          expires: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        })
        setStatus("authenticated")

        if (userData.user?.id) {
          localStorage.setItem("roomvia_userid", userData.user.id)
          await refreshCredits()
        }
      } else {
      }
    } catch (error) {
      console.error("💥 AuthContext: Update error:", error)
    }
  }

  return (
    <AuthContext.Provider value={{ data: session, status, update, refreshCredits, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

// Hook that provides session data
export function useSession() {
  return useContext(AuthContext)
}

// Server-side auth function
export async function auth() {
  return null
}
